import { getApiBaseUrl } from "../config/runtimeConfig";
import type { WorkVariationDto } from "../workVariations/workVariationsApi";
import { invalidateLookup } from "../lookups/lookupsApi";

const apiBaseUrl = getApiBaseUrl();

export class ApiError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(ApiError.humanize(message));
    this.status = status;
  }

  // ASP.NET error responses arrive as a ProblemDetails/ValidationProblemDetails
  // JSON body; surface the human-readable message instead of the raw JSON string.
  private static humanize(message: string): string {
    const trimmed = message?.trim();
    if (!trimmed || (trimmed[0] !== "{" && trimmed[0] !== "[")) return message;
    try {
      const parsed = JSON.parse(trimmed) as {
        detail?: string;
        title?: string;
        errors?: Record<string, string[]>;
      };
      const validationMessages = parsed.errors
        ? Object.values(parsed.errors).flat().filter(Boolean)
        : [];
      if (validationMessages.length > 0) return validationMessages.join(" ");
      return parsed.detail || parsed.title || message;
    } catch {
      return message;
    }
  }
}

export interface Work {
  id: number;
  code: string;
  name: string;
  location?: string | null;
  clientId: number;
  clientName: string;
  vatRate: number;
  startDate?: string | null;
  endDate?: string | null;
  progressPercent?: number | null;
  totalValue?: number | null;
  spentValue?: number | null;
  workStatusId: number;
  statusCode: string;
  statusName: string;
  budgetId?: number | null;
  budgetReference?: string | null;
  created: string;
  lastModified: string;
}

export interface WorkStatus {
  id: number;
  code: string;
  name: string;
  sortOrder: number;
}

export interface WorkItemStatus {
  id: number;
  code: string;
  name: string;
  sortOrder: number;
}

export interface WorkBudgetVersion {
  id: number;
  versionNumber: number;
  versionLabel?: string | null;
  isActiveVersion: boolean;
  status: string;
  totalAmount?: number | null;
  createdBy?: string | null;
  createdByName?: string | null;
  created: string;
}

export interface WorkOrderItemDelivery {
  id: number;
  lote: string | null;
  plannedDate: string | null;
  plannedQuantity: number;
  deliveredQuantity: number;
  notes: string | null;
  created: string;
}

export interface WorkOrderItem {
  id: number;
  workId: number;
  code: string;
  type?: string | null;
  location?: string | null;
  description: string;
  unit: string;
  quantity: number;
  wasteQuantity: number;
  workItemStatusId: number;
  statusCode: string;
  statusName: string;
  needDate?: string | null;
  deliveryDate?: string | null;
  deliveryWarehouseId?: number | null;
  deliveryWarehouseName?: string | null;
  receptionResponsible?: string | null;
  deliveryNotes?: string | null;
  unitPrice: number;
  supplier?: string | null;
  deliveries: WorkOrderItemDelivery[];
  balance: number;
  imputedCostId?: number | null;
  imputedAmount?: number | null;
  created: string;
  lastModified: string;
}

export interface AddWorkOrderDeliveryPayload {
  lote?: string | null;
  plannedDate?: string | null;
  plannedQuantity: number;
  deliveredQuantity: number;
  notes?: string | null;
}

export interface WorkSubcontractorItem {
  id: number;
  workId: number;
  code: string;
  type?: string | null;
  location?: string | null;
  description: string;
  subcontractor: string;
  value: number;
  workItemStatusId: number;
  statusCode: string;
  statusName: string;
  needDate?: string | null;
  imputedCostId?: number | null;
  imputedAmount?: number | null;
  created: string;
  lastModified: string;
}

export interface UpdateWorkPayload {
  clientId: number;
  name: string;
  location?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  progressPercent?: number | null;
  totalValue?: number | null;
  spentValue?: number | null;
  workStatusId: number;
}

export interface UpdateWorkOrderPayload {
  code: string;
  type?: string | null;
  location?: string | null;
  description: string;
  unit: string;
  quantity: number;
  wasteQuantity: number;
  workItemStatusId: number;
  needDate?: string | null;
  deliveryDate?: string | null;
  deliveryWarehouseId?: number | null;
  receptionResponsible?: string | null;
  deliveryNotes?: string | null;
  unitPrice: number;
  supplier?: string | null;
}

export interface UpdateWorkSubcontractorPayload {
  code: string;
  type?: string | null;
  location?: string | null;
  description: string;
  subcontractor: string;
  value: number;
  workItemStatusId: number;
  needDate?: string | null;
}

// Criar aceita o estado em falta — o servidor usa o primeiro por ordem, para não obrigar
// a escolher um estado antes de se ter escrito o que quer que seja.
export interface CreateWorkSubcontractorPayload
  extends Omit<UpdateWorkSubcontractorPayload, "workItemStatusId"> {
  workItemStatusId?: number | null;
}

async function request<T>(
  path: string,
  options?: RequestInit,
): Promise<T> {
  const response = await fetch(`${apiBaseUrl}${path}`, {
    ...options,
    credentials: "include",
  });

  if (!response.ok) {
    const text = await response.text();
    throw new ApiError(
      response.status,
      text || response.statusText,
    );
  }

  return (await response.json()) as T;
}

async function downloadWorkExport(
  workId: number,
  path: string,
  fallbackFileName: string,
): Promise<void> {
  const response = await fetch(
    `${apiBaseUrl}/api/works/${workId}/${path}`,
    { credentials: "include" },
  );

  if (!response.ok) {
    const text = await response.text();
    throw new ApiError(response.status, text || response.statusText);
  }

  const blob = await response.blob();
  const disposition = response.headers.get("content-disposition");
  let fileName = fallbackFileName;

  if (disposition) {
    const match = disposition.match(
      /filename\*?=(?:UTF-8'')?["']?([^"';\n]+)/i,
    );
    if (match?.[1]) {
      fileName = decodeURIComponent(match[1]);
    }
  }

  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

export function exportWorkOrdersToExcel(workId: number): Promise<void> {
  return downloadWorkExport(
    workId,
    "orders/export/excel",
    `Encomendas_${workId}.xlsx`,
  );
}

export function exportWorkOrdersToPdf(workId: number): Promise<void> {
  return downloadWorkExport(
    workId,
    "orders/export/pdf",
    `Encomendas_${workId}.pdf`,
  );
}

export function exportWorkSubcontractorsToExcel(
  workId: number,
): Promise<void> {
  return downloadWorkExport(
    workId,
    "subcontractors/export/excel",
    `SubEmpreiteiros_${workId}.xlsx`,
  );
}

export function exportWorkSubcontractorsToPdf(
  workId: number,
): Promise<void> {
  return downloadWorkExport(
    workId,
    "subcontractors/export/pdf",
    `SubEmpreiteiros_${workId}.pdf`,
  );
}

export function getWorks(): Promise<Work[]> {
  return request<Work[]>("/api/works");
}

// Projeção reduzida (id, código, nome) — única forma da lista de obras aberta à
// equipa interna; a Folha de Horas usa-a para não depender do getWorks completo.
export interface WorkOption {
  id: number;
  code: string;
  name: string;
}

export function getWorkOptions(): Promise<WorkOption[]> {
  return request<WorkOption[]>("/api/works/options");
}

export function getWorkById(workId: number): Promise<Work> {
  return request<Work>(`/api/works/${workId}`);
}

export function getWorkStatuses(): Promise<WorkStatus[]> {
  return request<WorkStatus[]>("/api/works/statuses");
}

export function getWorkItemStatuses(): Promise<WorkItemStatus[]> {
  return request<WorkItemStatus[]>("/api/works/item-statuses");
}

export function getWorkBudgetVersions(
  budgetId: number,
): Promise<WorkBudgetVersion[]> {
  return request<WorkBudgetVersion[]>(
    `/api/budgets/${budgetId}/versions`,
  );
}

export interface CreatedBudgetVersion {
  id: number;
}

export function createWorkBudgetVersion(
  budgetId: number,
  versionLabel?: string,
): Promise<CreatedBudgetVersion> {
  return request<CreatedBudgetVersion>(`/api/budgets/${budgetId}/versions`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(
      versionLabel !== undefined ? { versionLabel } : {},
    ),
  });
}

export function updateWork(
  workId: number,
  payload: UpdateWorkPayload,
): Promise<Work> {
  return request<Work>(`/api/works/${workId}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  }).then((result) => {
    invalidateLookup("works");
    return result;
  });
}

export function getWorkOrders(
  workId: number,
): Promise<WorkOrderItem[]> {
  return request<WorkOrderItem[]>(`/api/works/${workId}/orders`);
}

export function updateWorkOrder(
  workId: number,
  orderId: number,
  payload: UpdateWorkOrderPayload,
): Promise<WorkOrderItem> {
  return request<WorkOrderItem>(
    `/api/works/${workId}/orders/${orderId}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    },
  );
}

export function addWorkOrderDelivery(
  workId: number,
  orderId: number,
  payload: AddWorkOrderDeliveryPayload,
): Promise<WorkOrderItem> {
  return request<WorkOrderItem>(
    `/api/works/${workId}/orders/${orderId}/deliveries`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    },
  );
}

export function deleteWorkOrderDelivery(
  workId: number,
  orderId: number,
  deliveryId: number,
): Promise<WorkOrderItem> {
  return request<WorkOrderItem>(
    `/api/works/${workId}/orders/${orderId}/deliveries/${deliveryId}`,
    {
      method: "DELETE",
    },
  );
}

export function getWorkSubcontractors(
  workId: number,
): Promise<WorkSubcontractorItem[]> {
  return request<WorkSubcontractorItem[]>(
    `/api/works/${workId}/subcontractors`,
  );
}

export function updateWorkSubcontractor(
  workId: number,
  itemId: number,
  payload: UpdateWorkSubcontractorPayload,
): Promise<WorkSubcontractorItem> {
  return request<WorkSubcontractorItem>(
    `/api/works/${workId}/subcontractors/${itemId}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    },
  );
}

export interface CreateWorkPayload {
  name: string;
  clientId: number;
  location?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  totalValue?: number | null;
  vatRate?: number;
}

// Criar uma obra diretamente, sem orçamento associado. A via clássica continua a ser
// POST /api/budgets/{id}/work a partir de um orçamento aceite.
export function createWork(payload: CreateWorkPayload): Promise<Work> {
  return request<Work>(`/api/works`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  }).then((result) => {
    invalidateLookup("works");
    return result;
  });
}

// Acrescentar uma linha de subempreitada a uma obra já existente. Até aqui as linhas só nasciam
// do orçamento (WorkFromBudgetFactory); uma obra sem orçamento com subempreiteiros ficava sem
// linhas e, como o auto mede sempre contra uma linha, sem forma de criar autos.
export function createWorkSubcontractor(
  workId: number,
  payload: CreateWorkSubcontractorPayload,
): Promise<WorkSubcontractorItem> {
  return request<WorkSubcontractorItem>(`/api/works/${workId}/subcontractors`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
}

// [4.4.3] Imputar / anular o custo de material de uma linha do Mapa de Encomendas.
export function imputeWorkOrderCost(
  workId: number,
  orderId: number,
): Promise<WorkOrderItem> {
  return request<WorkOrderItem>(
    `/api/works/${workId}/orders/${orderId}/impute-cost`,
    { method: "POST" },
  );
}

export function removeWorkOrderCostImputation(
  workId: number,
  orderId: number,
): Promise<WorkOrderItem> {
  return request<WorkOrderItem>(
    `/api/works/${workId}/orders/${orderId}/impute-cost`,
    { method: "DELETE" },
  );
}

// [4.4.4] Imputar / anular o custo de subempreitada de uma linha do Mapa de Subempreiteiros.
export function imputeWorkSubcontractorCost(
  workId: number,
  itemId: number,
): Promise<WorkSubcontractorItem> {
  return request<WorkSubcontractorItem>(
    `/api/works/${workId}/subcontractors/${itemId}/impute-cost`,
    { method: "POST" },
  );
}

export function removeWorkSubcontractorCostImputation(
  workId: number,
  itemId: number,
): Promise<WorkSubcontractorItem> {
  return request<WorkSubcontractorItem>(
    `/api/works/${workId}/subcontractors/${itemId}/impute-cost`,
    { method: "DELETE" },
  );
}

export type WorkFinancialDocumentKind = "Auto" | "Revenue" | "Expense";
export type WorkFinancialEntryType = "Revenue" | "Expense";
export type WorkFinancialProcessingStatus =
  | "Pending"
  | "Processing"
  | "Ready"
  | "Failed";
export type WorkFinancialValidationStatus =
  | "PendingValidation"
  | "Validated";

export type WorkFinancialSupplierMatchState =
  | "Linked"
  | "Suggested"
  | "NotFound"
  | "NoTaxNumber";

// [2.1.9] Deteção de anomalias — informativa, não bloqueante.
export interface FinancialAnomaly {
  code: string;
  message: string;
  severity: "warning" | "danger";
}

// [2.1.7] Estados do workflow de aprovação (recebida/aprovada/paga/contestada).
export interface FinancialApprovalStatus {
  id: number;
  code: string;
  name: string;
  sortOrder: number;
}

export interface WorkFinancialEntry {
  id: number;
  workId: number;
  workFinancialDocumentId: number;
  entryType: WorkFinancialEntryType;
  documentNumber?: string | null;
  documentDate?: string | null;
  entityName?: string | null;
  entityTaxNumber?: string | null;
  description?: string | null;
  baseAmount?: number | null;
  vatAmount?: number | null;
  totalAmount?: number | null;
  currencyCode: string;
  confidence?: number | null;
  validationStatus: WorkFinancialValidationStatus;
  validatedAt?: string | null;
  // Lote A-18 — financial document intelligence
  expenseCategoryId?: number | null;
  expenseCategoryCode?: string | null;
  expenseCategoryName?: string | null;
  suggestedExpenseCategoryCode?: string | null;
  suggestedExpenseCategoryName?: string | null;
  matchedSupplierId?: number | null;
  matchedSupplierName?: string | null;
  suggestedSupplierId?: number | null;
  suggestedSupplierName?: string | null;
  supplierMatchState: WorkFinancialSupplierMatchState;
  isPotentialDuplicate: boolean;
  duplicateOfEntryIds?: number[] | null;
  // Lote A-19 — ponte fatura → custo (WorkCost source-linked)
  imputedCostId?: number | null;
  imputedAmount?: number | null;
  // Lote A-20 — leitura do QR code AT
  qrCodeRead?: boolean;
  atcud?: string | null;
  atVerificationCode?: string | null;
  // Lote A-22 [2.1.7] — workflow de aprovação
  approvalStatusId?: number | null;
  approvalStatusCode: string;
  approvalStatusName: string;
  // Lote A-22 [2.1.9] — anomalias detetadas (informativas)
  anomalies: FinancialAnomaly[];
}

export interface ExpenseCategory {
  id: number;
  code: string;
  name: string;
  sortOrder: number;
}

export interface WorkFinancialDocument {
  id: number;
  workId: number;
  fileName: string;
  contentType: string;
  sizeInBytes: number;
  documentKindHint: WorkFinancialDocumentKind;
  processingStatus: WorkFinancialProcessingStatus;
  processingError?: string | null;
  created: string;
  lastModified: string;
  entry?: WorkFinancialEntry | null;
}

export interface UpdateWorkFinancialEntryPayload {
  entryType: WorkFinancialEntryType;
  documentNumber?: string | null;
  documentDate?: string | null;
  entityName?: string | null;
  entityTaxNumber?: string | null;
  description?: string | null;
  baseAmount?: number | null;
  vatAmount?: number | null;
  totalAmount?: number | null;
  currencyCode?: string | null;
}

export function getWorkFinancialDocuments(
  workId: number,
): Promise<WorkFinancialDocument[]> {
  return request<WorkFinancialDocument[]>(
    `/api/works/${workId}/financial-documents`,
  );
}

export function uploadWorkFinancialDocument(
  workId: number,
  file: File,
  documentKindHint: WorkFinancialDocumentKind,
): Promise<WorkFinancialDocument> {
  const data = new FormData();
  data.append("file", file);
  data.append("documentKindHint", documentKindHint);

  return request<WorkFinancialDocument>(
    `/api/works/${workId}/financial-documents`,
    {
      method: "POST",
      body: data,
    },
  );
}

export function reprocessWorkFinancialDocument(
  workId: number,
  documentId: number,
): Promise<WorkFinancialDocument> {
  return request<WorkFinancialDocument>(
    `/api/works/${workId}/financial-documents/${documentId}/reprocess`,
    { method: "POST" },
  );
}

export function deleteWorkFinancialDocument(
  workId: number,
  documentId: number,
): Promise<void> {
  return request<void>(
    `/api/works/${workId}/financial-documents/${documentId}`,
    { method: "DELETE" },
  );
}

export function updateWorkFinancialEntry(
  workId: number,
  entryId: number,
  payload: UpdateWorkFinancialEntryPayload,
): Promise<WorkFinancialEntry> {
  return request<WorkFinancialEntry>(
    `/api/works/${workId}/financial-entries/${entryId}`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
  );
}

export function validateWorkFinancialEntry(
  workId: number,
  entryId: number,
): Promise<WorkFinancialEntry> {
  return request<WorkFinancialEntry>(
    `/api/works/${workId}/financial-entries/${entryId}/validate`,
    { method: "POST" },
  );
}

export function unvalidateWorkFinancialEntry(
  workId: number,
  entryId: number,
): Promise<WorkFinancialEntry> {
  return request<WorkFinancialEntry>(
    `/api/works/${workId}/financial-entries/${entryId}/unvalidate`,
    { method: "POST" },
  );
}

export function getWorkFinancialDocumentFileUrl(
  workId: number,
  documentId: number,
): string {
  return `${apiBaseUrl}/api/works/${workId}/financial-documents/${documentId}/file`;
}

export function getExpenseCategories(): Promise<ExpenseCategory[]> {
  return request<ExpenseCategory[]>("/api/expense-categories");
}

export function categorizeWorkFinancialEntry(
  workId: number,
  entryId: number,
  expenseCategoryId: number | null,
): Promise<WorkFinancialEntry> {
  return request<WorkFinancialEntry>(
    `/api/works/${workId}/financial-entries/${entryId}/categorize`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ expenseCategoryId }),
    },
  );
}

export function matchWorkFinancialEntrySupplier(
  workId: number,
  entryId: number,
  supplierId: number,
): Promise<WorkFinancialEntry> {
  return request<WorkFinancialEntry>(
    `/api/works/${workId}/financial-entries/${entryId}/match-supplier`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ supplierId }),
    },
  );
}

export function createSupplierFromWorkFinancialEntry(
  workId: number,
  entryId: number,
): Promise<WorkFinancialEntry> {
  return request<WorkFinancialEntry>(
    `/api/works/${workId}/financial-entries/${entryId}/create-supplier`,
    { method: "POST" },
  ).then((result) => {
    invalidateLookup("suppliers");
    return result;
  });
}

// [A-19] Ponte fatura → custo: imputar / anular o custo de uma despesa validada.
export function imputeWorkFinancialEntryCost(
  workId: number,
  entryId: number,
): Promise<WorkFinancialEntry> {
  return request<WorkFinancialEntry>(
    `/api/works/${workId}/financial-entries/${entryId}/impute-cost`,
    { method: "POST" },
  );
}

export function removeWorkFinancialEntryCostImputation(
  workId: number,
  entryId: number,
): Promise<WorkFinancialEntry> {
  return request<WorkFinancialEntry>(
    `/api/works/${workId}/financial-entries/${entryId}/impute-cost`,
    { method: "DELETE" },
  );
}

// [2.1.7] Workflow de aprovação de documentos financeiros.
export function getFinancialApprovalStatuses(): Promise<FinancialApprovalStatus[]> {
  return request<FinancialApprovalStatus[]>(
    "/api/works/financial-entries/approval-statuses",
  );
}

export function setWorkFinancialEntryApprovalStatus(
  workId: number,
  entryId: number,
  statusCode: string,
): Promise<WorkFinancialEntry> {
  return request<WorkFinancialEntry>(
    `/api/works/${workId}/financial-entries/${entryId}/approval-status`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ statusCode }),
    },
  );
}

export type FinancialStatus = "NoData" | "Healthy" | "Attention" | "Critical";

export interface FinancialDashboardTotals {
  budgetedAmount?: number | null;
  budgetSource: string;
  revenueAmount: number;
  validatedRevenueAmount: number;
  pendingRevenueAmount: number;
  expenseAmount: number;
  validatedExpenseAmount: number;
  pendingExpenseAmount: number;
  realMarginAmount: number;
  realMarginPercent?: number | null;
  revenueDeviationAmount?: number | null;
  revenueDeviationPercent?: number | null;
  pendingValidationAmount: number;
  pendingValidationCount: number;
}

export interface FinancialAlert {
  severity: FinancialStatus;
  code: string;
  message: string;
}

export interface MonthlyFinancialSeriesPoint {
  year: number;
  month: number;
  label: string;
  revenueAmount: number;
  expenseAmount: number;
  marginAmount: number;
}

export interface FinancialDashboardEntry {
  id: number;
  workId: number;
  workFinancialDocumentId: number;
  entryType: WorkFinancialEntryType;
  documentNumber?: string | null;
  documentDate?: string | null;
  entityName?: string | null;
  description?: string | null;
  totalAmount?: number | null;
  currencyCode: string;
  validationStatus: WorkFinancialValidationStatus;
  confidence?: number | null;
  fileName?: string | null;
}

export interface WorkFinancialDashboard {
  workId: number;
  workCode: string;
  workName: string;
  clientName: string;
  workStatus?: string | null;
  financialStatus: FinancialStatus;
  totals: FinancialDashboardTotals;
  alerts: FinancialAlert[];
  entries: FinancialDashboardEntry[];
  monthlySeries: MonthlyFinancialSeriesPoint[];
}

export interface FinancialDashboardWorkRow {
  workId: number;
  workCode: string;
  workName: string;
  clientId: number;
  clientName: string;
  workStatus?: string | null;
  created: string;
  financialStatus: FinancialStatus;
  totals: FinancialDashboardTotals;
  alerts: FinancialAlert[];
}

export interface FinancialDashboardWorks {
  totals: FinancialDashboardTotals;
  activeWorksCount: number;
  riskWorksCount: number;
  works: FinancialDashboardWorkRow[];
}

export interface FinancialDashboardWorksFilters {
  clientId?: number | null;
  workStatus?: string | null;
  financialStatus?: FinancialStatus | null;
  hasPendingValidation?: boolean | null;
  onlyCritical?: boolean | null;
  fromDate?: string | null;
  toDate?: string | null;
}

export function getWorkFinancialDashboard(
  workId: number,
): Promise<WorkFinancialDashboard> {
  return request<WorkFinancialDashboard>(`/api/works/${workId}/financial-dashboard`);
}

export function getFinancialDashboardWorks(
  filters: FinancialDashboardWorksFilters = {},
): Promise<FinancialDashboardWorks> {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== "") params.set(key, String(value));
  });
  const query = params.toString();
  return request<FinancialDashboardWorks>(`/api/financial-dashboard/works${query ? `?${query}` : ""}`);
}

export interface BillingClientTotal {
  clientName: string;
  total: number;
}

export interface BillingDashboard {
  currentMonthTotal: number;
  sameMonthLastYearTotal: number;
  currentYearToDateTotal: number;
  priorYearToDateTotal: number;
  topClients: BillingClientTotal[];
}

export function getBillingDashboard(topN?: number | null): Promise<BillingDashboard> {
  const query = topN !== null && topN !== undefined ? `?topN=${topN}` : "";
  return request<BillingDashboard>(`/api/financial-dashboard/billing${query}`);
}

// [2.3.2] Conciliação fiscal — IVA suportado vs liquidado por período (cross-obra).
export interface TaxReconciliationPeriod {
  periodKey: string;
  periodLabel: string;
  year: number;
  periodNumber: number;
  revenueBase: number;
  vatCollected: number;
  expenseBase: number;
  vatPaid: number;
  vatBalance: number;
  revenueCount: number;
  expenseCount: number;
}

export interface TaxReconciliationTotals {
  revenueBase: number;
  vatCollected: number;
  expenseBase: number;
  vatPaid: number;
  vatBalance: number;
  revenueCount: number;
  expenseCount: number;
}

export interface TaxReconciliation {
  granularity: string;
  periods: TaxReconciliationPeriod[];
  totals: TaxReconciliationTotals;
}

export interface TaxReconciliationParams {
  granularity: string;
  year?: number | null;
  includeUnvalidated?: boolean;
}

function taxReconciliationQuery(params: TaxReconciliationParams): string {
  const search = new URLSearchParams();
  search.set("granularity", params.granularity);
  if (params.year !== null && params.year !== undefined) {
    search.set("year", String(params.year));
  }
  if (params.includeUnvalidated !== undefined) {
    search.set("includeUnvalidated", String(params.includeUnvalidated));
  }
  return search.toString();
}

export function getTaxReconciliation(
  params: TaxReconciliationParams,
): Promise<TaxReconciliation> {
  const query = taxReconciliationQuery(params);
  return request<TaxReconciliation>(
    `/api/financial-dashboard/tax-reconciliation${query ? `?${query}` : ""}`,
  );
}

// GET → blob download. Lê o filename do Content-Disposition (espelha downloadWorkExport).
export async function downloadTaxReconciliation(
  params: TaxReconciliationParams,
  format: "excel" | "csv",
): Promise<void> {
  const query = taxReconciliationQuery(params);
  const response = await fetch(
    `${apiBaseUrl}/api/financial-dashboard/tax-reconciliation/export/${format}${query ? `?${query}` : ""}`,
    { credentials: "include" },
  );

  if (!response.ok) {
    const text = await response.text();
    throw new ApiError(response.status, text || response.statusText);
  }

  const blob = await response.blob();
  const disposition = response.headers.get("content-disposition");
  let fileName = format === "excel" ? "conciliacao-fiscal.xlsx" : "conciliacao-fiscal.csv";

  if (disposition) {
    const match = disposition.match(/filename\*?=(?:UTF-8'')?["']?([^"';\n]+)/i);
    if (match?.[1]) {
      fileName = decodeURIComponent(match[1]);
    }
  }

  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

// [4.5.x] Mapa de Custos — orçamento vs realização (derrapagem) por área/capítulo/artigo.

export interface CostMapArticle {
  code: string;
  description: string;
  unit: string;
  quantity: number;
  sell: number;
  budgetedCost: number;
  actualCost: number;
  deviation: number;
  deviationPercent: number | null;
  margin: number;
  marginPercent: number | null;
  hasBudgetedCost: boolean;
}

export interface CostMapChapter {
  name: string;
  sell: number;
  budgetedCost: number;
  actualCost: number;
  deviation: number;
  deviationPercent: number | null;
  margin: number;
  marginPercent: number | null;
  hasBudgetedCost: boolean;
  articles: CostMapArticle[];
}

export interface CostMapArea {
  name: string;
  sell: number;
  budgetedCost: number;
  actualCost: number;
  deviation: number;
  deviationPercent: number | null;
  margin: number;
  marginPercent: number | null;
  chapters: CostMapChapter[];
}

export interface WorkCostMap {
  workId: number;
  sell: number;
  budgetedCost: number;
  actualCost: number;
  deviation: number;
  deviationPercent: number | null;
  margin: number;
  marginPercent: number | null;
  overrunThresholdPercent: number;
  overrunChapterCount: number;
  unmappedActualCost: number;
  areas: CostMapArea[];
}

export interface WorkBudgetChapter {
  areaName: string;
  chapterName: string;
}

export function getWorkCostMap(workId: number): Promise<WorkCostMap> {
  return request<WorkCostMap>(`/api/works/${workId}/cost-map`);
}

export function getWorkBudgetChapters(
  workId: number,
): Promise<WorkBudgetChapter[]> {
  return request<WorkBudgetChapter[]>(`/api/works/${workId}/budget-chapters`);
}

// [4.6.3] Balanço final (post-mortem) — orçamento vs realizado, margem e prazo,
// mais notas de causas de desvio registadas no encerramento da obra.
export interface WorkPostMortemDto {
  workId: number;
  workName: string;
  statusCode: string;
  statusName: string;
  isClosed: boolean;
  sell: number;
  budgetedCost: number;
  actualCost: number;
  plannedMargin: number;
  plannedMarginPercent?: number | null;
  actualMargin: number;
  actualMarginPercent?: number | null;
  marginVariance: number;
  startDate?: string | null;
  plannedEndDate?: string | null;
  actualEndDate?: string | null;
  plannedDurationDays?: number | null;
  actualDurationDays?: number | null;
  scheduleVarianceDays?: number | null;
  closureNotes?: string | null;
  // Lote 41 [4.12.9]/[4.7.1] — janela de garantia pós-encerramento.
  warrantyMonths?: number | null;
  warrantyEndDate?: string | null;
  isUnderWarranty: boolean;
}

export interface CloseWorkPayload {
  warrantyMonthsOverride?: number;
  closureNotes?: string;
}

export function getWorkPostMortem(
  workId: number,
): Promise<WorkPostMortemDto> {
  return request<WorkPostMortemDto>(`/api/works/${workId}/post-mortem`);
}

// [4.12.9] Encerra a obra: regista a data de fim, abre o período de garantia e
// devolve o post-mortem atualizado. warrantyMonthsOverride é opcional (o backend
// aplica o default das settings quando omitido).
export function closeWork(
  workId: number,
  payload: CloseWorkPayload,
): Promise<WorkPostMortemDto> {
  return request<WorkPostMortemDto>(`/api/works/${workId}/close`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export function saveWorkClosureNotes(
  workId: number,
  notes: string | null,
): Promise<WorkPostMortemDto> {
  return request<WorkPostMortemDto>(`/api/works/${workId}/closure-notes`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ notes }),
  });
}

// [4.8.2] Mapa Final da Obra — superset do post-mortem com a cascata de
// variações (orçamento base → revisto) e a lista de variações registadas, para
// a vista consolidada do Balanço Final.
export interface WorkFinalMapDto extends WorkPostMortemDto {
  approvedAdditions: number;
  approvedReductions: number;
  revisedSell: number;
  revisedMargin: number;
  revisedMarginPercent?: number | null;
  variations: WorkVariationDto[];
}

export function getWorkFinalMap(workId: number): Promise<WorkFinalMapDto> {
  return request<WorkFinalMapDto>(`/api/works/${workId}/final-map`);
}

// [2.2.2] Autos de medição e entrega — intermediate autos + final delivery auto, obra-scoped.
export type MeasurementAutoType = "intermediate" | "final";
// Lifecycle draft → approved → issued (aprovação e faturação progressiva).
export type MeasurementAutoStatus = "draft" | "approved" | "issued";

export interface MeasurementAutoLineDto {
  id: number;
  sortOrder: number;
  description: string;
  unit: string | null;
  quantity: number;
  unitPrice: number;
  amount: number;
}

export interface MeasurementAutoDto {
  id: number;
  workId: number;
  number: string | null;
  autoType: MeasurementAutoType;
  autoDate: string;
  periodStart?: string | null;
  periodEnd?: string | null;
  clientName: string | null;
  notes: string | null;
  status: MeasurementAutoStatus;
  approvedAt?: string | null;
  totalAmount: number;
  // Fatura viva gerada a partir deste auto (null = ainda não faturado).
  invoiceId?: number | null;
  invoiceNumber?: string | null;
  lines: MeasurementAutoLineDto[];
  created: string;
  lastModified: string;
}

export interface MeasurementAutoLinePayload {
  description: string;
  unit?: string | null;
  quantity: number;
  unitPrice: number;
  sortOrder?: number;
}

export interface MeasurementAutoPayload {
  autoType: MeasurementAutoType;
  autoDate: string;
  periodStart?: string | null;
  periodEnd?: string | null;
  number?: string | null;
  clientName?: string | null;
  notes?: string | null;
  status?: MeasurementAutoStatus;
  lines: MeasurementAutoLinePayload[];
}

// Subset devolvido por generate-invoice (espelha o ClientInvoiceDto gerado; local
// para manter obrasApi.ts desacoplado do web-api-client). Só os campos que a UI usa.
export interface ClientInvoiceDto {
  id: number;
  number?: string | null;
  totalAmount?: number | null;
  statusCode?: string | null;
  statusName?: string | null;
  measurementAutoId?: number | null;
  measurementAutoNumber?: string | null;
}

export function getMeasurementAutos(
  workId: number,
): Promise<MeasurementAutoDto[]> {
  return request<MeasurementAutoDto[]>(
    `/api/works/${workId}/measurement-autos`,
  );
}

export function getMeasurementAutoById(
  workId: number,
  autoId: number,
): Promise<MeasurementAutoDto> {
  return request<MeasurementAutoDto>(
    `/api/works/${workId}/measurement-autos/${autoId}`,
  );
}

export function createMeasurementAuto(
  workId: number,
  payload: MeasurementAutoPayload,
): Promise<MeasurementAutoDto> {
  return request<MeasurementAutoDto>(
    `/api/works/${workId}/measurement-autos`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
  );
}

export function updateMeasurementAuto(
  workId: number,
  autoId: number,
  payload: MeasurementAutoPayload,
): Promise<MeasurementAutoDto> {
  return request<MeasurementAutoDto>(
    `/api/works/${workId}/measurement-autos/${autoId}`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
  );
}

export async function deleteMeasurementAuto(
  workId: number,
  autoId: number,
): Promise<void> {
  const response = await fetch(
    `${apiBaseUrl}/api/works/${workId}/measurement-autos/${autoId}`,
    { method: "DELETE", credentials: "include" },
  );

  if (!response.ok) {
    const text = await response.text();
    throw new ApiError(response.status, text || response.statusText);
  }
}

export function exportMeasurementAutoToPdf(
  workId: number,
  autoId: number,
): Promise<void> {
  return downloadWorkExport(
    workId,
    `measurement-autos/${autoId}/export/pdf`,
    `Auto_${autoId}.pdf`,
  );
}

// Aprovar um auto (só a partir de draft) → passa a "approved".
export function approveMeasurementAuto(
  workId: number,
  autoId: number,
): Promise<MeasurementAutoDto> {
  return request<MeasurementAutoDto>(
    `/api/works/${workId}/measurement-autos/${autoId}/approve`,
    { method: "POST" },
  );
}

// Reabrir um auto aprovado → volta a "draft" (bloqueado se já tiver fatura viva).
export function reopenMeasurementAuto(
  workId: number,
  autoId: number,
): Promise<MeasurementAutoDto> {
  return request<MeasurementAutoDto>(
    `/api/works/${workId}/measurement-autos/${autoId}/reopen`,
    { method: "POST" },
  );
}

// Faturação progressiva: gera um rascunho de fatura a partir de um auto approved/issued.
export function generateInvoiceFromAuto(
  workId: number,
  autoId: number,
): Promise<ClientInvoiceDto> {
  return request<ClientInvoiceDto>(
    `/api/works/${workId}/measurement-autos/${autoId}/generate-invoice`,
    { method: "POST" },
  );
}
